import { TFunction } from "i18next";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { SVG_MUI } from "@ryde/assets";
import { CheckboxUser, HubRideCompanionMark } from "@ryde/controls";
import {
  calculatePassengersInfoSeatsCount,
  EMPTY_VALUE_DISPLAY,
  getRidePassengerStationTime,
  IPassengerInfo,
  IRideDto,
  isHubRideCompanionPassenger,
  NO_VALUE_DISPLAY,
  noop,
  useFormats,
} from "@ryde/shared";

import { KanbanGridPassengerRowStyled } from "../rides.kanban.item.styled";

interface IGetPassengersLabelArguments {
  passenger: IPassengerInfo;
  seatsCount: number;
  t: TFunction;
}

const getPassengersLabel = ({
  passenger,
  seatsCount,
  t,
}: IGetPassengersLabelArguments): string => {
  if (seatsCount < 2) {
    const passFirstName = `${passenger?.firstName || EMPTY_VALUE_DISPLAY} ${passenger?.lastName || EMPTY_VALUE_DISPLAY}`;
    return passFirstName;
  }

  return t("ride.DRAGGING_PASSENGERS_COUNT", {
    numPassengers: seatsCount,
  });
};

interface IRidesKanbanItemPassengersRowDragOverlayProps {
  passenger: IPassengerInfo;
  rideDto: Partial<IRideDto>;
  dragSelectedIds?: number[];
}

export const RidesKanbanItemPassengersRowDragOverlay: FC<
  IRidesKanbanItemPassengersRowDragOverlayProps
> = ({ rideDto, passenger, dragSelectedIds }) => {
  const { id: passengerId } = passenger;
  const { formatTimeNormalized } = useFormats();
  const { t } = useTranslation();
  const stationTime = useMemo(() => {
    const timeValue = getRidePassengerStationTime(passengerId, rideDto);
    return timeValue ? formatTimeNormalized(timeValue) : NO_VALUE_DISPLAY;
  }, [formatTimeNormalized, passengerId, rideDto]);

  const seatsCount = useMemo(() => {
    const dragSelectedIdsSet = dragSelectedIds
      ? new Set(dragSelectedIds)
      : null;
    const passengers = rideDto?.metadata?.passengersInfo?.filter(
      x => dragSelectedIdsSet?.has(x.id) || passengerId === x.id,
    );
    return calculatePassengersInfoSeatsCount(passengers || []);
  }, [dragSelectedIds, passengerId, rideDto?.metadata?.passengersInfo]);

  const dragPassengersLabel = getPassengersLabel({
    passenger,
    seatsCount,
    t,
  });
  const showCompanionMark =
    seatsCount < 2 &&
    isHubRideCompanionPassenger({
      passenger,
      rideDto,
    });

  return (
    <KanbanGridPassengerRowStyled
      $isDragClone
      data-e2e-id={`pass_${passengerId}`}
      data-e2e-is-drag-clone="yes"
    >
      <span className="drag_grip">
        <SVG_MUI.GripVerticalSVG />
      </span>
      <span className="checkbox_user">
        <CheckboxUser checked setChecked={noop} />
      </span>
      <span className="time">{stationTime}</span>
      <span className="family_name_wrap">
        {showCompanionMark && (
          <HubRideCompanionMark className="hub-ride-companion-mark" />
        )}
        <span className="family_name truncate-text">{dragPassengersLabel}</span>
      </span>
    </KanbanGridPassengerRowStyled>
  );
};
